import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaSave, FaLayerGroup } from 'react-icons/fa';

const GroupModal = ({ isOpen, onClose, group, userInfo, API_BASE_URL, auth, onSave, onNotification }) => {
  const [formData, setFormData] = useState({
    group_name: '',
    description: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (group) {
        // Edit mode
        setFormData({
          group_name: group.group_name || '',
          description: group.description || '',
          status: group.status || 'active'
        });
      } else {
        // Add mode - reset form
        setFormData({
          group_name: '',
          description: '',
          status: 'active'
        });
      }
      setErrors({});
    }
  }, [isOpen, group]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.group_name.trim()) {
      newErrors.group_name = 'Group name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Validate zone assignment
    if (!userInfo.zone_id) {
      alert('You must be assigned to a zone to manage groups. Please contact your administrator.');
      return;
    }

    setLoading(true);
    try {
      const groupData = {
        ...formData,
        mmda_id: userInfo.mmda_id,
        zone_id: userInfo.zone_id,
        created_by: userInfo.user_id
      };

      let response;

      if (group) {
        // Update existing group
        response = await axios.put(
          `${API_BASE_URL}/zonal-groups/${group.group_id}`,
          groupData,
          auth
        );

        console.log('Group update response:', response.data);

        // Show success notification for update
        console.log('Setting group update notification...');
        if (onNotification) {
          onNotification({
            show: true,
            type: 'success',
            title: 'Group Updated Successfully',
            message: response.data.message || `${formData.group_name || 'Group'} has been updated successfully.`
          });
        }
        console.log('Group update notification set');
      } else {
        // Create new group
        response = await axios.post(
          `${API_BASE_URL}/zonal-groups`,
          groupData,
          auth
        );

        console.log('Group create response:', response.data);

        // Show success notification for create
        console.log('Setting group create notification...');
        if (onNotification) {
          onNotification({
            show: true,
            type: 'success',
            title: 'Group Added Successfully',
            message: response.data.message || `${formData.group_name || 'New group'} has been added successfully.`
          });
        }
        console.log('Group create notification set');
      }

      // Call onSave after a delay to ensure notification is shown
      setTimeout(() => {
        onSave();
      }, 100);
      // Don't close immediately, let user see the success message
    } catch (error) {
      console.error('Error saving group:', error);
      console.error('Error response:', error.response?.data);

      // Show error notification
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          'Failed to save group. Please try again.';

      if (onNotification) {
        onNotification({
          show: true,
          type: 'error',
          title: group ? 'Update Failed' : 'Add Group Failed',
          message: errorMessage
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main Modal */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaLayerGroup color="#9b59b6" />
            {group ? 'Edit Group' : 'Add New Group'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#7f8c8d'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '2rem' }}>
            {/* Group Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Group Name *
              </label>
              <input
                type="text"
                name="group_name"
                value={formData.group_name}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${errors.group_name ? '#e74c3c' : '#ddd'}`,
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
                placeholder="Enter group name"
              />
              {errors.group_name && (
                <span style={{ color: '#e74c3c', fontSize: '0.8rem' }}>{errors.group_name}</span>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
                placeholder="Enter group description (optional)"
              />
            </div>

            {/* Status */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.25rem',
                  fontSize: '1rem'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Zone Info */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '0.25rem',
              marginBottom: '1rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>Group Location</h4>
              <p style={{ margin: 0, color: '#7f8c8d', fontSize: '0.9rem' }}>
                <strong>MMDA:</strong> {userInfo.mmda_name || 'Current MMDA'}<br />
                <strong>Zone:</strong> {userInfo.zone_name || 'Current Zone'}
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading ? '#95a5a6' : '#9b59b6',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaSave />
              {loading ? 'Saving...' : 'Save Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
      )}


    </>
  );
};

export default GroupModal;
